import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  Observable,
  mergeMap,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import { DateTime } from 'luxon';
import type DataSource from 'devextreme/data/data_source';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { APP_CONFIG } from '@application/config';
import { SettingsStore } from '@settings/data-access';
import { SelfUserStore } from '@domains/person/user/self/data';
import {
  initialTreasuryStoreState,
  BackendEntryType,
  CashFlowAggregatesScope,
  CashFlowEntry,
  CashFlowMovement,
  CashFlowPaymentMethod,
  CashHomeChartsRequest,
  CashSession,
  FinancialAccount,
  FlowDirection,
  isAdjustmentEntryType,
  TenantDailyAggregatePoint,
  TreasuryCloseSessionRequest,
  TreasuryCreateCashOperationRequest,
  TreasuryCreateCashTransferRequest,
  TreasuryDateRange,
  TreasuryFinalizeSessionRequest,
  TreasuryHomeRequest,
  TreasuryOpenSessionRequest,
  TreasuryReopenSessionRequest,
  TreasuryStoreState,
} from './entities/treasury.model';
import {
  TreasuryService,
  type TreasuryCashFlowEntriesScope,
} from './infra/treasury.service';
import { PersonLookupService } from './infra/person-lookup.service';
import {
  TreasuryCashTurnContextService,
  type TreasuryCashTurnLoadRaw,
} from './infra/treasury-cash-turn-context.service';
import { CashSessionStatus, normalizeCashSessionStatus, readCashSessionId } from '@domains/treasury/util';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { tapResponse } from '@ngrx/operators';
import { getDateTime } from '@shared/utils';

/** Window size (days) for the operator's "recent sessions" home section. */
export const OPERATOR_HISTORY_WINDOW_DAYS = 30;

/** Default sparkline window for the home daily summary cards (today + 6 prior). */
export const TENANT_DAILY_SERIES_WINDOW_DAYS = 7;

/**
 * Normalize a possibly-datetime ISO string to its `yyyy-MM-dd` date part.
 * Returns null if the input is not a parseable date.
 */
const isoDateOnly = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const head = value.length >= 10 ? value.slice(0, 10) : value;
  const dt = getDateTime(head);
  return dt?.isValid ? dt.toISODate() : null;
};

/**
 * Inclusive list of ISO `yyyy-MM-dd` strings between `range.StartDate` and
 * `range.EndDate`. Order: oldest → newest. Returns `[]` if either bound is
 * unparseable or the range is inverted.
 */
const enumerateDaysInclusive = (range: TreasuryDateRange): string[] => {
  const start = getDateTime(range.StartDate);
  const end = getDateTime(range.EndDate);
  if (!start?.isValid || !end?.isValid || end < start) return [];
  const days: string[] = [];
  for (let cursor = start; cursor <= end; cursor = cursor.plus({ days: 1 })) {
    const iso = cursor.toISODate();
    if (iso) days.push(iso);
  }
  return days;
};

export const TreasuryStore = signalStore(
  { providedIn: 'root' },
  withState<TreasuryStoreState>(initialTreasuryStoreState),
  withReqState(
    'openCashSession',
    'closeCashSession',
    'finalizeCashSession',
    'reopenCashSession',
    'createCashOperation',
    'createCashTransfer',
    'getCashSessionById',
    'loadCashTurnContext',
    'loadFinancialAccounts',
    'loadCashHomeChartsData',
    'loadOpenSessions',
    'loadPendingSessions',
    'loadMyRecentSessions',
    'loadCashFlowReport',
    'loadTenantDailyReport',
    'loadTenantDailySeries',
    'loadReconciliationDashboard',
    'loadMyOpenSession',
    'loadSessionCashFlowEntries',
    'loadCashFlowAggregates',
    'loadCashFlowEntriesByDay',
  ),
  withComputed((
    store,
    settingsStore = inject(SettingsStore),
  ) => {
    const culture = computed(() => settingsStore.ownerSettings()?.Culture ?? 'pt-BR');

    const accountById = computed(() => {
      const map = new Map<string, FinancialAccount>();
      for (const account of store.financialAccounts()) {
        map.set(account.FinancialAccountId, account);
      }
      return map;
    });

    /**
     * Tenant-wide daily aggregates (server-side $apply groupby) feeding the
     * three "today" computeds. Each point already carries
     * (Direction, EntryType, PaymentMethod, FinancialAccountId, Total, Count);
     * consumers collapse whichever dimensions they don't need.
     */
    const tenantAggregatePoints = computed<TenantDailyAggregatePoint[]>(
      () => store.tenantDailyAggregates() ?? [],
    );

    /** Lines from the account-scoped cash-flow report — used by per-session aggregates (sessionTotalsById). */
    const movements = computed<CashFlowMovement[]>(() => {
      const report = store.cashFlowReport();
      return report?.Lines ?? [];
    });

    const dailyTotals = computed(() => {
      const totals = {
        entries: 0,
        exits: 0,
        adjustments: 0,
        transfers: 0,
        entriesCount: 0,
        exitsCount: 0,
        adjustmentsCount: 0,
        transfersCount: 0,
      };
      for (const point of tenantAggregatePoints()) {
        const amount = Math.abs(point.Total ?? 0);
        const count = point.Count ?? 0;
        const isTransfer = point.EntryType === BackendEntryType.Transfer;
        if (!isTransfer && point.Direction === FlowDirection.In) {
          totals.entries += amount;
          totals.entriesCount += count;
        }
        if (!isTransfer && point.Direction === FlowDirection.Out) {
          totals.exits += amount;
          totals.exitsCount += count;
        }
        if (isAdjustmentEntryType(point.EntryType)) {
          totals.adjustments += amount;
          totals.adjustmentsCount += count;
        }
        if (
          point.EntryType === BackendEntryType.Transfer &&
          point.Direction === FlowDirection.Out
        ) {
          totals.transfers += amount;
          totals.transfersCount += count;
        }
      }
      return totals;
    });

    /**
     * Daily distribution across the requested {@link tenantDailySeriesRange}
     * window for each of the 4 daily cards.
     *
     * Buckets mirror the classification in {@link dailyTotals}: a single
     * aggregated point can contribute to multiple series (e.g. a point with
     * Direction=In AND EntryType=Adjustment lands in both `entries[d]` and
     * `adjustments[d]`). The 4 cards are rendered as independent filter views,
     * not as a stacked chart — the overlap is intentional. **Transfer** rows are
     * excluded from `entries`/`exits` (both legs are internal moves); outbound
     * transfer volume is only in `transfers`, matching {@link dailyTotals}.
     *
     * `days` is the ordered list of ISO date strings (oldest → newest) that
     * indexes the parallel `entries`/`exits`/`adjustments`/`transfers` arrays.
     * Days with no movement still appear with value 0 — the backend simply
     * omits such rows, the client zero-fills.
     *
     * When no window has been requested yet, returns 7 empty buckets so the
     * UI can render a flat sparkline placeholder consistently.
     */
    const dailyTotalsDailySeries = computed(() => {
      const range = store.tenantDailySeriesRange();
      const days = range ? enumerateDaysInclusive(range) : [];
      const length = days.length || 7;
      const buckets = {
        days,
        entries: Array<number>(length).fill(0),
        exits: Array<number>(length).fill(0),
        adjustments: Array<number>(length).fill(0),
        transfers: Array<number>(length).fill(0),
      };
      if (days.length === 0) return buckets;

      const indexByDay = new Map(days.map((day, idx) => [day, idx]));
      for (const point of store.tenantDailySeries()) {
        const day = isoDateOnly(point.CompetenceDate);
        const idx = day !== null ? indexByDay.get(day) : undefined;
        if (idx === undefined) continue;
        const amount = Math.abs(point.Total ?? 0);
        const isTransfer = point.EntryType === BackendEntryType.Transfer;
        if (!isTransfer && point.Direction === FlowDirection.In) {
          buckets.entries[idx] += amount;
        }
        if (!isTransfer && point.Direction === FlowDirection.Out) {
          buckets.exits[idx] += amount;
        }
        if (isAdjustmentEntryType(point.EntryType)) buckets.adjustments[idx] += amount;
        if (
          point.EntryType === BackendEntryType.Transfer &&
          point.Direction === FlowDirection.Out
        ) {
          buckets.transfers[idx] += amount;
        }
      }
      return buckets;
    });

    /**
     * Per-day-per-account breakdown matching the same classification as
     * {@link dailyTotalsDailySeries}. For each of the 4 keys, returns one
     * pre-sorted (amount desc) array of `{FinancialAccountId, Amount, Count}`
     * per day of the requested window — same indexing as `days`. The sparkline
     * tooltip uses these arrays to reveal the account composition behind each
     * data point.
     *
     * Built from the same {@link tenantDailySeries} payload that powers the
     * line trace, so the breakdown sums always match the headline value for
     * a given day (no second round-trip, no drift).
     */
    const dailyTotalsByAccountDailySeries = computed(() => {
      const range = store.tenantDailySeriesRange();
      const days = range ? enumerateDaysInclusive(range) : [];
      const length = days.length || 7;
      type Bucket = Map<string, { amount: number; count: number }>;
      const makeDayBuckets = () =>
        Array.from({ length }, () => new Map() as Bucket);
      const buckets: Record<
        'entries' | 'exits' | 'adjustments' | 'transfers',
        Bucket[]
      > = {
        entries: makeDayBuckets(),
        exits: makeDayBuckets(),
        adjustments: makeDayBuckets(),
        transfers: makeDayBuckets(),
      };
      const accumulate = (bucket: Bucket, accountId: string, amount: number, count: number) => {
        const current = bucket.get(accountId) ?? { amount: 0, count: 0 };
        current.amount += amount;
        current.count += count;
        bucket.set(accountId, current);
      };

      if (days.length > 0) {
        const indexByDay = new Map(days.map((day, idx) => [day, idx]));
        for (const point of store.tenantDailySeries()) {
          const day = isoDateOnly(point.CompetenceDate);
          const idx = day !== null ? indexByDay.get(day) : undefined;
          if (idx === undefined) continue;
          const accountId = point.FinancialAccountId;
          if (!accountId) continue;
          const amount = Math.abs(point.Total ?? 0);
          const count = point.Count ?? 0;
          const isTransfer = point.EntryType === BackendEntryType.Transfer;
          if (!isTransfer && point.Direction === FlowDirection.In) {
            accumulate(buckets.entries[idx], accountId, amount, count);
          }
          if (!isTransfer && point.Direction === FlowDirection.Out) {
            accumulate(buckets.exits[idx], accountId, amount, count);
          }
          if (isAdjustmentEntryType(point.EntryType)) {
            accumulate(buckets.adjustments[idx], accountId, amount, count);
          }
          if (
            point.EntryType === BackendEntryType.Transfer &&
            point.Direction === FlowDirection.Out
          ) {
            accumulate(buckets.transfers[idx], accountId, amount, count);
          }
        }
      }

      const toSortedArray = (bucket: Bucket) =>
        Array.from(bucket.entries())
          .map(([FinancialAccountId, { amount, count }]) => ({
            FinancialAccountId,
            Amount: amount,
            Count: count,
          }))
          .sort((a, b) => b.Amount - a.Amount);

      return {
        days,
        entries: buckets.entries.map(toSortedArray),
        exits: buckets.exits.map(toSortedArray),
        adjustments: buckets.adjustments.map(toSortedArray),
        transfers: buckets.transfers.map(toSortedArray),
      };
    });

    /**
     * Per-key breakdown of the same buckets used by {@link dailyTotals}, sliced
     * by originating financial account. Each entry carries the account id, the
     * summed (absolute) amount, and the movement count — pre-sorted by amount
     * descending so the UI renders the largest contributors first. Rows
     * without `FinancialAccountId` are skipped (defensive — server should
     * always include it on tenant-scoped reports).
     */
    const dailyTotalsByAccount = computed(() => {
      type Bucket = Map<string, { amount: number; count: number }>;
      const buckets: Record<
        'entries' | 'exits' | 'adjustments' | 'transfers',
        Bucket
      > = {
        entries: new Map(),
        exits: new Map(),
        adjustments: new Map(),
        transfers: new Map(),
      };
      const accumulate = (
        bucket: Bucket,
        accountId: string,
        amount: number,
        count: number,
      ) => {
        const current = bucket.get(accountId) ?? { amount: 0, count: 0 };
        current.amount += amount;
        current.count += count;
        bucket.set(accountId, current);
      };

      for (const point of tenantAggregatePoints()) {
        const accountId = point.FinancialAccountId;
        if (!accountId) continue;
        const amount = Math.abs(point.Total ?? 0);
        const count = point.Count ?? 0;
        const isTransfer = point.EntryType === BackendEntryType.Transfer;
        if (!isTransfer && point.Direction === FlowDirection.In) {
          accumulate(buckets.entries, accountId, amount, count);
        }
        if (!isTransfer && point.Direction === FlowDirection.Out) {
          accumulate(buckets.exits, accountId, amount, count);
        }
        if (isAdjustmentEntryType(point.EntryType)) {
          accumulate(buckets.adjustments, accountId, amount, count);
        }
        if (
          point.EntryType === BackendEntryType.Transfer &&
          point.Direction === FlowDirection.Out
        ) {
          accumulate(buckets.transfers, accountId, amount, count);
        }
      }

      const toSortedArray = (bucket: Bucket) =>
        Array.from(bucket.entries())
          .map(([FinancialAccountId, { amount, count }]) => ({
            FinancialAccountId,
            Amount: amount,
            Count: count,
          }))
          .sort((a, b) => b.Amount - a.Amount);

      return {
        entries: toSortedArray(buckets.entries),
        exits: toSortedArray(buckets.exits),
        adjustments: toSortedArray(buckets.adjustments),
        transfers: toSortedArray(buckets.transfers),
      };
    });

    const dailyByPaymentMethod = computed(() => {
      const empty = { in: 0, out: 0, count: 0 };
      const acc: Record<CashFlowPaymentMethod, { in: number; out: number; count: number }> = {
        [CashFlowPaymentMethod.Cash]: { ...empty },
        [CashFlowPaymentMethod.Card]: { ...empty },
        [CashFlowPaymentMethod.Pix]: { ...empty },
        [CashFlowPaymentMethod.BankSlip]: { ...empty },
        [CashFlowPaymentMethod.Check]: { ...empty },
      };
      for (const point of tenantAggregatePoints()) {
        const method = point.PaymentMethod;
        if (method === null || method === undefined) continue;
        const bucket = acc[method];
        if (!bucket) continue;
        const amount = Math.abs(point.Total ?? 0);
        const count = point.Count ?? 0;
        if (point.Direction === FlowDirection.In) bucket.in += amount;
        if (point.Direction === FlowDirection.Out) bucket.out += amount;
        bucket.count += count;
      }
      return acc;
    });

    const sessionTotalsById = computed(() => {
      const map = new Map<
        string,
        { entries: number; exits: number; moved: number; count: number }
      >();
      for (const row of movements()) {
        const sessionId = row.CashSessionId;
        if (!sessionId) continue;
        const amount = Math.abs(row.Amount ?? 0);
        const current = map.get(sessionId) ?? {
          entries: 0,
          exits: 0,
          moved: 0,
          count: 0,
        };
        if (row.Direction === FlowDirection.In) current.entries += amount;
        if (row.Direction === FlowDirection.Out) current.exits += amount;
        current.moved += amount;
        current.count += 1;
        map.set(sessionId, current);
      }
      const odataCounts = store.sessionCashFlowEntryCountById();
      for (const [sessionId, count] of Object.entries(odataCounts)) {
        const current = map.get(sessionId) ?? {
          entries: 0,
          exits: 0,
          moved: 0,
          count: 0,
        };
        current.count = count;
        map.set(sessionId, current);
      }
      return map;
    });

    return {
      culture,
      myOpenSession: computed(() => store.myOpenSessions()[0] ?? null),
      accountById,
      dailyTotals,
      dailyTotalsDailySeries,
      dailyTotalsByAccount,
      dailyTotalsByAccountDailySeries,
      dailyByPaymentMethod,
      sessionTotalsById,
      /**
       * Operator's own *closed* sessions in the recent window — open-cash-select
       * shows them as "recently closed" entries. Derived from the
       * `myRecentSessions` state slot (which holds all statuses).
       */
      myClosedRecentSessions: computed(() =>
        store.myRecentSessions().filter((s) => {
          return (
            normalizeCashSessionStatus(s.Status) !== CashSessionStatus.Open
          );
        }),
      ),
    };
  }),
  withMethods(
    (
      store,
      treasuryService = inject(TreasuryService),
      personLookup = inject(PersonLookupService),
      cashTurnContext = inject(TreasuryCashTurnContextService),
      dxOdataService = inject(DxOdataStoreService),
      url = inject(APP_CONFIG).apiUrl,
      selfUserStore = inject(SelfUserStore),
    ) => {
      const loadFinancialAccounts = rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, {
              ...setPropLoading('loadFinancialAccounts'),
            });
            return treasuryService.getFinancialAccounts().pipe(
              tapResponse({
                next: (financialAccounts) =>
                patchState(store, {
                  financialAccounts,
                  ...setPropLoaded('loadFinancialAccounts'),
                }),
                error: (error) => patchState(store, {
                  ...setPropError('loadFinancialAccounts', error),
                })
              })
            );
          }),
        ),
      );

      /**
       * The three home cash-session slots load independently — `openSessions`,
       * `pendingSessions`, and `myRecentSessions` each have their own rxMethod
       * with its own request-state (`loadOpenSessions*`, `loadPendingSessions*`,
       * `loadMyRecentSessions*`). This lets the home UI render a skeleton per
       * card and surface a failure in one slot without blanking the others.
       *
       * Replaces the legacy `loadCashSessions` which fired
       * `OPTIONS /CashSession` with no filter and then N parallel
       * `countCashFlowEntriesForSession` calls.
       */
      const loadOpenSessions = rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, setPropLoading('loadOpenSessions'));
            return treasuryService.getOpenCashSessions().pipe(
              // After the open list lands, fire the bounded
              // `getCashFlowEntryCountsForSessions` over those ids so the
              // "N movimentos" badge is fresh too. A failure here falls back
              // to `{}` so the open list still renders.
              switchMap((open) => {
                patchState(store, { openSessions: open });
                mergePersonNames(
                  open
                    .flatMap((s) => [s.OpenedBy ?? null, s.ClosedBy ?? null])
                    .filter((id): id is string => !!id),
                );
                const openIds = [
                  ...new Set(
                    open
                      .map((s) => readCashSessionId(s))
                      .filter((id): id is string => id !== null),
                  ),
                ];
                return treasuryService
                  .getCashFlowEntryCountsForSessions(openIds)
                  .pipe(catchError(() => of({} as Record<string, number>)));
              }),
              tapResponse({
                next: (sessionCashFlowEntryCountById) =>
                  patchState(
                    store,
                    { sessionCashFlowEntryCountById },
                    setPropLoaded('loadOpenSessions'),
                  ),
                error: (error) =>
                  patchState(store, setPropError('loadOpenSessions', error)),
              }),
            );
          }),
        ),
      );

      /**
       * `pendingSessions` is sorted by absolute `Difference` desc at load
       * time so the home card always surfaces the largest discrepancies
       * first — consumers can read the slot directly without re-sorting.
       */
      const loadPendingSessions = rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, setPropLoading('loadPendingSessions'));
            return treasuryService.getPendingCashSessions().pipe(
              tapResponse({
                next: (pending) => {
                  const sorted = [...pending].sort(
                    (left, right) =>
                      Math.abs(right.Difference ?? 0) -
                      Math.abs(left.Difference ?? 0),
                  );
                  patchState(store, {
                    pendingSessions: sorted,
                    ...setPropLoaded('loadPendingSessions'),
                  });
                  mergePersonNames(
                    sorted
                      .flatMap((s) => [s.OpenedBy ?? null, s.ClosedBy ?? null])
                      .filter((id): id is string => !!id),
                  );
                },
                error: (error) =>
                  patchState(
                    store,
                    setPropError('loadPendingSessions', error),
                  ),
              }),
            );
          }),
        ),
      );

      /**
       * If the operator's `PersonId` isn't known yet (race with the auth
       * bootstrap), this short-circuits to `[]` rather than firing a
       * malformed filter — the caller can re-invoke once auth resolves.
       */
      const loadMyRecentSessions = rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, setPropLoading('loadMyRecentSessions'));
            const personId = selfUserStore.userData()?.PersonId ?? null;
            // Wall-clock-as-UTC convention (same shape as `todayRange()` in
            // the home feature): the cutoff is emitted with the operator's
            // local wall-clock labelled as `Z`. `DxOdataStoreService`'s
            // `adjustODataFilterDatesToUtc` unfolds it back to the real UTC
            // instant on the way out, so `OpenedOnUtc ge <cutoff>` lines up
            // with the operator's "30 days ago" intent across timezones.
            const cutoffIso =
              DateTime.now()
                .minus({ days: OPERATOR_HISTORY_WINDOW_DAYS })
                .toISO({ suppressMilliseconds: true }) ?? '';
            if (!personId || !cutoffIso) {
              patchState(store, {
                myRecentSessions: [],
                ...setPropLoaded('loadMyRecentSessions'),
              });
              return of(void 0);
            }
            return treasuryService
              .getMyRecentCashSessions({
                operatorPersonId: personId,
                fromUtc: cutoffIso,
              })
              .pipe(
                tapResponse({
                  next: (myRecent) => {
                    patchState(store, {
                      myRecentSessions: myRecent,
                      ...setPropLoaded('loadMyRecentSessions'),
                    });
                    mergePersonNames(
                      myRecent
                        .flatMap((s) => [s.OpenedBy ?? null, s.ClosedBy ?? null])
                        .filter((id): id is string => !!id),
                    );
                  },
                  error: (error) =>
                    patchState(
                      store,
                      setPropError('loadMyRecentSessions', error),
                    ),
                }),
              );
          }),
        ),
      );

      /**
       * Convenience trigger for callers (home `ngOnInit`/refresh, transfer
       * post-action) that want to refresh all three home slots at once. Each
       * rxMethod fires its own HTTP request in parallel; their request-states
       * remain independent so the UI can skeleton/error per card.
       */
      const loadHomeCashSessions = (): void => {
        loadOpenSessions();
        loadPendingSessions();
        loadMyRecentSessions();
      };

      /**
       * Server-side DataSource for the per-account "Histórico de caixa"
       * sidebar. The page owns the DataSource instance so it can drive
       * pagination (via `ds.pageIndex(N).load()`) and append the new page to
       * the rendered list as the operator scrolls — see
       * `treasury-financial-account-page-feature`'s `loadMoreAccountSessions`.
       *
       * No store slot is maintained for the sessions themselves; the page
       * accumulates rows locally. Other consumers that historically read
       * `store.accountSessions()` (the cash-turn-feature lookup) fall through
       * to the open / pending / myRecent slots which `dispatchSessionToSlots`
       * keeps in sync after mutations.
       */
      const accountCashSessionsDataSource = (
        financialAccountId: string,
      ): DataSource<CashSession> =>
        treasuryService.accountCashSessionsDataSource(financialAccountId);

      const loadCashFlowReport = rxMethod<TreasuryHomeRequest>(
        pipe(
          switchMap((request) => {
            patchState(store, setPropLoading('loadCashFlowReport'));
            return treasuryService.getCashFlowReport(request).pipe(
              tapResponse({
                next: (lines) => {
                  patchState(store, {
                    cashFlowReport: {
                      FinancialAccountId: request?.FinancialAccountId ?? '',
                      Lines: lines,
                    },
                    ...setPropLoaded('loadCashFlowReport'),
                  });
                },
                error: (error) =>
                  patchState(store, {
                    cashFlowReport: null,
                    ...setPropError(
                      'loadCashFlowReport',
                      error,
                    ),
                  }),
              }),
            );
          }),
        ),
      );

      /**
       * Loads the tenant-wide totals that feed the home "today" cards.
       * Server-side `$apply` groupby (see `getTenantDailyAggregates`) — the
       * legacy implementation pulled every entry of the day with `$top=1000`
       * just to client-reduce 4 sums; the new path returns a bounded matrix
       * of `(Direction, EntryType, PaymentMethod, FinancialAccountId)`
       * aggregates that the home computeds collapse on demand.
       */
      const loadTenantDailyReport = rxMethod<{ StartDate: string; EndDate: string }>(
        pipe(
          switchMap((request) => {
            patchState(store, setPropLoading('loadTenantDailyReport'));
            return treasuryService.getTenantDailyAggregates({
              StartDate: request.StartDate,
              EndDate: request.EndDate,
            }).pipe(
              tapResponse({
                next: (points) =>
                  patchState(store, {
                    tenantDailyAggregates: points,
                    ...setPropLoaded('loadTenantDailyReport'),
                  }),
                error: (error) =>
                  patchState(store, {
                    tenantDailyAggregates: null,
                    ...setPropError(
                      'loadTenantDailyReport',
                      error,
                    ),
                  }),
              }),
            );
          }),
        ),
      );

      /**
       * Loads the 7-day server-aggregated sparkline series. Failure is
       * intentionally swallowed: the home banner and the today-scoped
       * cards keep working, the sparkline simply renders flat zeros.
       */
      const loadTenantDailySeries = rxMethod<TreasuryDateRange>(
        pipe(
          switchMap((request) => {
            patchState(store, {
              tenantDailySeriesRange: request,
              ...setPropLoading('loadTenantDailySeries'),
            });
            return treasuryService.getTenantDailySeries(request).pipe(
              tapResponse({
                next: (points) =>
                  patchState(store, {
                    tenantDailySeries: points,
                    ...setPropLoaded('loadTenantDailySeries'),
                  }),
                error: (error) =>
                  patchState(store, {
                    tenantDailySeries: [],
                    ...setPropError('loadTenantDailySeries', error),
                  }),
              }),
            );
          }),
        ),
      );

      const loadReconciliationDashboard = rxMethod<TreasuryHomeRequest>(
        pipe(
          switchMap((request) => {
            patchState(store, setPropLoading('loadReconciliationDashboard'));
            return treasuryService.getReconciliationDashboard(request).pipe(
              tapResponse({
                next: (reconciliationDashboard) =>
                  patchState(store, {
                    reconciliationDashboard,
                    ...setPropLoaded('loadReconciliationDashboard'),
                  }),
                error: (error) =>
                  patchState(store, {
                    reconciliationDashboard: null,
                    ...setPropError(
                      'loadReconciliationDashboard',
                      error,
                    ),
                  }),
              }),
            );
          }),
        ),
      );

      const loadMyOpenSession = rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, setPropLoading('loadMyOpenSession'));
            return treasuryService.getMyOpenSessions().pipe(
              tapResponse({
                next: (sessions) =>
                  patchState(store, {
                    myOpenSessions: sessions,
                    ...setPropLoaded('loadMyOpenSession'),
                  }),
                error: (error) =>
                  patchState(store, {
                    myOpenSessions: [],
                    ...setPropError(
                      'loadMyOpenSession',
                      error,
                    ),
                  }),
              }),
            );
          }),
        ),
      );

      /**
       * Routes a fresh `CashSession` into each slot it belongs to based on
       * the session's current `Status` and `OpenedBy` (a single session can
       * live in multiple slots simultaneously — e.g. Open + opened-by-me sits
       * in both `openSessions` and `myRecentSessions`). When the session's
       * state has changed such that a slot no longer applies (e.g. a session
       * transitioned Open → Closing), this also removes it from that slot —
       * so consumers never see a stale row in the wrong bucket.
       *
       * `accountSessions` is kept in sync only if the session belongs to the
       * account currently loaded into that slot; otherwise it's untouched.
       */
      const dispatchSessionToSlots = (session: CashSession): void => {
        if (!session?.CashSessionId) return;
        const status = normalizeCashSessionStatus(session.Status);
        const personId = selfUserStore.userData()?.PersonId ?? null;
        const cutoffMs = DateTime.now()
          .minus({ days: OPERATOR_HISTORY_WINDOW_DAYS })
          .toMillis();
        const openedMs = getDateTime(session.OpenedOnUtc)?.toMillis() ?? Number.NaN;
        const isMyRecent =
          personId != null &&
          session.OpenedBy === personId &&
          Number.isFinite(openedMs) &&
          openedMs >= cutoffMs;

        const upsertOrRemove = (
          list: CashSession[],
          shouldBeIn: boolean,
        ): CashSession[] => {
          const idx = list.findIndex(
            (s) => s.CashSessionId === session.CashSessionId,
          );
          if (shouldBeIn) {
            return idx >= 0
              ? list.map((s) =>
                  s.CashSessionId === session.CashSessionId ? session : s,
                )
              : [...list, session];
          }
          return idx >= 0
            ? list.filter((s) => s.CashSessionId !== session.CashSessionId)
            : list;
        };

        // `pendingSessions` carries an ordering invariant (`|Difference|` desc).
        // Re-applying it here keeps the slot consumer-ready after every mutation.
        // The per-account history sidebar owns its own paginated DataSource
        // (see `accountCashSessionsDataSource`) so mutations are only reflected
        // there on the next reload — acceptable trade-off for the simpler
        // infinite-scroll path.
        const nextPending = [
          ...upsertOrRemove(
            store.pendingSessions(),
            status === CashSessionStatus.Closing ||
              status === CashSessionStatus.PendingReview,
          ),
        ].sort(
          (left, right) =>
            Math.abs(right.Difference ?? 0) - Math.abs(left.Difference ?? 0),
        );

        patchState(store, {
          openSessions: upsertOrRemove(
            store.openSessions(),
            status === CashSessionStatus.Open,
          ),
          pendingSessions: nextPending,
          myRecentSessions: upsertOrRemove(
            store.myRecentSessions(),
            isMyRecent,
          ),
        });
      };

      const refreshCashSession = (sessionId: string): void => {
        treasuryService.getCashSession(sessionId).subscribe({
          next: (session) => {
            if (session) {
              dispatchSessionToSlots(session);
            }
          },
          error: () => undefined,
        });
      };

      // Outer combinator is `mergeMap` (not `switchMap`) because each batch
      // carries IDs from a different source (grid `changed`, report load,
      // turn context, …) and must run to completion. With `switchMap`, a
      // second batch fired milliseconds after the first cancels the in-flight
      // `resolveNames`, and since `PersonLookupService` only writes its cache
      // in the final `toArray`, the cancelled batch is lost entirely — the
      // detail aside then resolves `personNameById.get(id)` to `undefined`.
      const mergePersonNames = rxMethod<string[]>(
        pipe(
          mergeMap((ids) => {
            const unique = [
              ...new Set(
                ids.filter((id): id is string =>
                  Boolean(id && String(id).trim()),
                ),
              ),
            ];
            if (unique.length === 0) {
              return of(void 0);
            }
            return personLookup.resolveNames(unique).pipe(
              tap((resolvedMap) => {
                const merged = new Map(store.personNameById());
                for (const [id, name] of resolvedMap.entries()) {
                  if (name) {
                    merged.set(id, name);
                  }
                }
                patchState(store, { personNameById: merged });
              }),
              switchMap(() => of(void 0)),
            );
          }),
        ),
      );

      const resetHome = (): void => {
        patchState(store, initialTreasuryStoreState);
      };

      const loadSessionCashFlowEntries = rxMethod<string>(
        pipe(
          switchMap((sessionId) => {
            patchState(store, setPropLoading('loadSessionCashFlowEntries'));
            return treasuryService.getSessionCashFlowEntries(sessionId).pipe(
              tapResponse({
                next: (entries: CashFlowEntry[]) => {
                  patchState(store, {
                    sessionCashFlowEntries: {
                      ...store.sessionCashFlowEntries(),
                      [sessionId]: entries,
                    },
                    ...setPropLoaded('loadSessionCashFlowEntries'),
                  });
                },
                error: (error) =>
                  patchState(
                    store,
                    setPropError(
                      'loadSessionCashFlowEntries',
                      error,
                    ),
                  ),
              }),
            );
          }),
        ),
      );


      const loadCashFlowAggregates = rxMethod<CashFlowAggregatesScope>(
        pipe(
          switchMap((scope) => {
            patchState(store, setPropLoading('loadCashFlowAggregates'));
            return treasuryService.getCashFlowAggregates(scope).pipe(
              tapResponse({
                next: (aggregates) =>
                  patchState(store, {
                    cashFlowAggregates: aggregates,
                    ...setPropLoaded('loadCashFlowAggregates'),
                  }),
                error: (error) =>
                  patchState(store, {
                    cashFlowAggregates: null,
                    ...setPropError('loadCashFlowAggregates', error),
                  }),
              }),
            );
          }),
        ),
      );

      const clearCashFlowAggregates = (): void => {
        patchState(store, { cashFlowAggregates: null });
      };

      const loadCashFlowEntriesByDay = rxMethod<{
        scope?: TreasuryCashFlowEntriesScope;
        gridFilter: string | null;
        loadedSpan: { minLocal: string; maxLocal: string };
        /** Account's `InitialBalance`, seeded into the day-banner running balance. */
        initialBalance?: number;
      }>(
        pipe(
          switchMap(({ scope, gridFilter, loadedSpan, initialBalance }) => {
            patchState(
              store,
              setPropLoading('loadCashFlowEntriesByDay'),
            );
            return treasuryService
              .getCashFlowEntriesByDay(
                scope,
                gridFilter,
                loadedSpan,
                initialBalance ?? 0,
              )
              .pipe(
                tapResponse({
                  next: (cashFlowEntriesByDay) =>
                    patchState(store, {
                      cashFlowEntriesByDay,
                      ...setPropLoaded('loadCashFlowEntriesByDay'),
                    }),
                  error: (error) =>
                    patchState(store, {
                      cashFlowEntriesByDay: null,
                      ...setPropError(
                        'loadCashFlowEntriesByDay',
                        error,
                      ),
                    }),
                }),
              );
          }),
        ),
      );

      const clearCashFlowEntriesByDay = (): void => {
        patchState(store, { cashFlowEntriesByDay: null });
      };

      /**
       * Server-side `DataSource` backed by `GET /OData/CashFlowEntries`,
       * scoped via optional `financialAccountId` / `cashSessionId`. The grid
       * pushes filter/sort/paging down to OData via `remoteOperations`.
       *
       * Mirrors the request's `$filter` into the summary cards on every
       * `changed` by dispatching {@link loadCashFlowAggregates} with the
       * same expression — the cards thus track the grid's header filters /
       * search in lockstep. Person-name resolution is deferred: the grid
       * doesn't render names, and the detail aside triggers a per-entry
       * `mergePersonNames` when the operator opens a row.
       *
       * Each call returns a fresh DataSource — memoize at the component level.
       */
      const cashFlowEntriesDataSource = (scope?: {
        financialAccountId?: string;
        cashSessionId?: string;
        /**
         * Forwarded to `loadCashFlowAggregates` on every grid load. Set to
         * `false` for accounts where the totalReconciled card is hidden
         * (cash accounts) so the second OData round-trip is skipped.
         * Defaults to `true` when omitted (account-detail bank flow).
         */
        includeReconciled?: boolean;
        /**
         * Account's `InitialBalance` (from `GET /FinancialAccount?id`). Always
         * seeded into the per-day running balance so the timeline banner shows
         * the real account balance, not just the net of its operations.
         */
        initialBalance?: number;
      }): DataSource<CashFlowEntry> => {
        const ds = treasuryService.cashFlowEntriesDataSource(scope);

        // Aggregates depend only on the effective `$filter` (+ fixed scope), not
        // on paging. Track the last dispatched filter so the cards re-query only
        // when the filter actually changes — never on a page (`skip`) change.
        // `undefined` sentinel (distinct from any real `string | null` filter)
        // forces the first load through.
        let lastAggregatesFilter: string | null | undefined;

        // The per-day query's running balance is scoped only by the account id
        // + window (it ignores the grid's filters so the balance is the real
        // account state), but the per-day separator key DOES honour the grid
        // filter so the banner lands on a visible row. So both the window and
        // the filter affect the result — track `filter|minLocal|maxLocal` so a
        // `changed` event that reproduces all three doesn't fire duplicate calls.
        let lastDayKey: string | undefined;

        ds.on('changed', () => {
          // `exposeRequest: true` (set in TreasuryService.cashFlowEntriesDataSource)
          // attaches a `request` signal that captures the OData request just sent.
          // Read it to learn the effective `$filter` (pre-scope from `fromOdata`
          // + runtime filter + search, AND-combined by DxOdataStoreService) and
          // feed the same expression to the aggregates query.
          const request = (ds as unknown as { request?: () => { params?: Record<string, string> } | null }).request;
          const rawFilter = request?.()?.params?.['$filter'] ?? null;
          if (rawFilter !== lastAggregatesFilter) {
            lastAggregatesFilter = rawFilter;
            loadCashFlowAggregates({
              financialAccountId: scope?.financialAccountId ?? null,
              rawFilter,
              includeReconciled: scope?.includeReconciled,
            });
          }

          // Extremos de `AddedOnUtc` na página carregada, em HORA DE PAREDE da
          // conta — não instantes UTC, apesar do nome da coluna: o
          // `formatUtcDatesInData` do `dxOdataStore` já os trouxe para o fuso da
          // conta, e é nessa forma que o `$filter` os quer (ver
          // `localDayStart`). O serviço alarga isto a dias locais inteiros,
          // portanto a perda de precisão do `Date → toISO` aqui é inócua: mexe
          // no dia, não no instante dentro dele.
          const loadedRows = ds.items?.() ?? [];
          const instants = loadedRows
            .map((row) => {
              if (!row.AddedOnUtc) return null;
              return getDateTime(row.AddedOnUtc)?.toISO() ?? null;
            })
            .filter((iso): iso is string => !!iso);
          if (instants.length === 0) {
            clearCashFlowEntriesByDay();
            return;
          }
          const toMillis = (iso: string) => getDateTime(iso)?.toMillis() || 0;
          const minLocal = instants.reduce((a, b) => (toMillis(a) < toMillis(b) ? a : b));
          const maxLocal = instants.reduce((a, b) => (toMillis(a) > toMillis(b) ? a : b));

          const dayKey = `${rawFilter}|${minLocal}|${maxLocal}`;
          if (dayKey === lastDayKey) return;
          lastDayKey = dayKey;
          loadCashFlowEntriesByDay({
            scope: { financialAccountId: scope?.financialAccountId },
            gridFilter: rawFilter,
            loadedSpan: { minLocal, maxLocal },
            initialBalance: scope?.initialBalance,
          });
        });

        return ds;
      };

      const invalidateSessionCashFlowEntries = (sessionId: string): void => {
        const cache = { ...store.sessionCashFlowEntries() };
        delete cache[sessionId];
        patchState(store, { sessionCashFlowEntries: cache });
      };

      const upsertSession = (session: CashSession | null | undefined): void => {
        if (!session?.CashSessionId) return;
        dispatchSessionToSlots(session);
      };

      const openCashSession = (
        request: TreasuryOpenSessionRequest,
      ): void => {
        patchState(store, setPropLoading('openCashSession'));
        treasuryService.openCashSession(request).subscribe({
          next: (session) => {
            if (session?.CashSessionId) {
              upsertSession(session);
            }
            patchState(store, setPropLoaded('openCashSession'));
          },
          error: (err) => {
            patchState(store, setPropError('openCashSession', err));
          },
        });
      };

      const closeCashSession = (
        request: TreasuryCloseSessionRequest,
      ): void => {
        patchState(store, setPropLoading('closeCashSession'));
        treasuryService.closeCashSession(request).subscribe({
          next: (session) => {
            if (session?.CashSessionId) {
              upsertSession(session);
            }
            patchState(store, setPropLoaded('closeCashSession'));
          },
          error: (err) => {
            patchState(store, setPropError('closeCashSession', err));
          },
        });
      };

      const finalizeCashSession = (
        request: TreasuryFinalizeSessionRequest,
      ): void => {
        patchState(store, setPropLoading('finalizeCashSession'));
        treasuryService.finalizeCashSession(request).subscribe({
          next: (session) => {
            if (session?.CashSessionId) {
              upsertSession(session);
            }
            patchState(store, setPropLoaded('finalizeCashSession'));
          },
          error: (err) => {
            patchState(store, setPropError('finalizeCashSession', err));
          },
        });
      };

      const reopenCashSession = (
        request: TreasuryReopenSessionRequest,
      ): void => {
        patchState(store, setPropLoading('reopenCashSession'));
        treasuryService.reopenCashSession(request).subscribe({
          next: (session) => {
            if (session?.CashSessionId) {
              upsertSession(session);
            }
            patchState(store, setPropLoaded('reopenCashSession'));
          },
          error: (err) => {
            patchState(store, setPropError('reopenCashSession', err));
          },
        });
      };

      const createCashOperation = (
        request: TreasuryCreateCashOperationRequest,
      ): void => {
        patchState(store, setPropLoading('createCashOperation'));
        treasuryService.createCashOperation(request).subscribe({
          next: (operation) => {
            patchState(store, setPropLoaded('createCashOperation'));
            if (operation?.CashOperationId) {
              const today = DateTime.local().toISODate() ?? '';
              loadTenantDailyReport({ StartDate: today, EndDate: today });
            }
          },
          error: (err) => {
            patchState(store, setPropError('createCashOperation', err));
          },
        });
      };

      const createCashTransfer = (
        request: TreasuryCreateCashTransferRequest,
      ): void => {
        patchState(store, setPropLoading('createCashTransfer'));
        treasuryService.createCashTransfer(request).subscribe({
          next: (transfer) => {
            patchState(store, setPropLoaded('createCashTransfer'));
            if (transfer?.CashTransferId) {
              loadHomeCashSessions();
              const today = DateTime.local().toISODate() ?? '';
              loadTenantDailyReport({ StartDate: today, EndDate: today });
            }
          },
          error: (err) => {
            patchState(
              store,
              setPropError(
                'createCashTransfer',
                err,
              ),
            );
          },
        });
      };

      const getCashSessionById = (sessionId: string): void => {
        patchState(store, setPropLoading('getCashSessionById'));
        treasuryService.getCashSession(sessionId).subscribe({
          next: (session) => {
            if (session?.CashSessionId) {
              upsertSession(session);
            }
            patchState(store, setPropLoaded('getCashSessionById'));
          },
          error: (err) => {
            patchState(store, setPropError('getCashSessionById', err));
          },
        });
      };

      const loadCashTurnContext = (
        params: {
          financialAccountId: string;
          cashSessionId: string | null;
        },
      ): void => {
        patchState(store, setPropLoading('loadCashTurnContext'));
        cashTurnContext.loadTurnContext(params).pipe(
          tapResponse({
            next: (raw) => {
              patchState(store, {
                turnContextLoadRaw: raw,
                sessionCashFlowEntries: raw.session
                  ? {
                      ...store.sessionCashFlowEntries(),
                      [raw.session.CashSessionId]: raw.entries,
                    }
                  : store.sessionCashFlowEntries(),
                ...setPropLoaded('loadCashTurnContext'),
              });
            },
            error: (err) =>
              patchState(
                store,
                setPropError(
                  'loadCashTurnContext',
                  err,
                ),
              ),
          }),
        ).subscribe();
      };

      const clearTurnContext = (): void => {
        patchState(store, { turnContextLoadRaw: null });
      };

      const resetCashSessionReqState = (): void => {
        patchState(
          store,
          setPropInit(
            'openCashSession',
            'closeCashSession',
            'finalizeCashSession',
            'reopenCashSession',
            'getCashSessionById',
          ),
        );
      };

      const resetCashOperationReqState = (): void => {
        patchState(store, setPropInit('createCashOperation', 'createCashTransfer'));
      };

      const resetCashTurnContextReqState = (): void => {
        patchState(store, setPropInit('loadCashTurnContext'));
      };

      /**
       * Server-side `DataSource` backed by `GET /OData/CashSessions`.
       * Filters, sort, paging, and search are all pushed down to OData via
       * `DxOdataStoreService.fromOdata`. Each call returns a fresh DataSource
       * — callers should memoize at the component level.
       *
       * Hooks the DataSource's `changed` event to resolve `OpenedBy`/`ClosedBy`
       * UUIDs → display names via {@link PersonLookupService} (cache-backed),
       * patching `personNameById` so the UI can render names instead of GUIDs.
       * The OData endpoint does not project person names, so this client-side
       * augmentation is the only path.
       */
      const getCashSessionsDataSource = (): DataSource<CashSession> => {
        const ds = dxOdataService.fromOdata<CashSession>({
          url: url.treasury_service,
          metadata: 'OData/CashSessions',
          key: 'CashSessionId',
          // Tell ODataStore which fields are Edm.Guid so runtime
          // `DataSource.filter([...])` clauses serialize them as bare UUIDs
          // (Edm.Guid literal) — without this, string values would be sent
          // single-quoted as Edm.String and the backend rejects with a
          // type-mismatch error. Consumers that need the bare UUID for
          // router navigation or Map lookups call `String(value)` on the
          // field (ODataStore wraps response values in `Guid` instances).
          fieldTypes: {
            CashSessionId: 'Guid',
            FinancialAccountId: 'Guid',
            OpenedBy: 'Guid',
            ClosedBy: 'Guid',
          },
        });

        ds.on('changed', () => {
          const items = (ds.items() ?? []) as CashSession[];
          const ids: string[] = [];
          // `OpenedBy`/`ClosedBy` arrive as DX `Guid` instances (per fieldTypes);
          // coerce with `String()` so cache keys + Map lookups stay primitive.
          for (const s of items) {
            if (s?.OpenedBy) ids.push(String(s.OpenedBy));
            if (s?.ClosedBy) ids.push(String(s.ClosedBy));
          }
          if (ids.length > 0) {
            mergePersonNames(ids);
          }
        });

        return ds;
      };

      const loadCashHomeChartsData = (request: CashHomeChartsRequest): void => {
        patchState(store, setPropLoading('loadCashHomeChartsData'));

        const conditions: string[] = [];
        if (request.startDate) {
          conditions.push(`OpenedOnUtc ge ${request.startDate}`);
        }
        if (request.endDate) {
          conditions.push(`OpenedOnUtc le ${request.endDate}`);
        }

        if (request.financialAccountId) {
          conditions.push(`FinancialAccountId eq ${request.financialAccountId}`);
        }
        
        if (request.operatorPersonId) {
          conditions.push(`OpenedBy eq ${request.operatorPersonId}`);
        }
        if (CashSessionStatus[request.sessionStatus]) {
          conditions.push(`SessionStatus eq '${request.sessionStatus}'`);
        }

        if (request.withoutDifference) {
          conditions.push(
            `(Difference eq 0)`,
          );
        }

        if (request.withDifference) {
          conditions.push(
            `(Difference ge 0.01 or Difference le -0.01)`,
          );
        }
        
        if (request.activityWindowStart || request.activityWindowEnd) {
          const parts: string[] = [];
          if (request.activityWindowStart) {
            parts.push(`OpenedOnUtc ge ${request.activityWindowStart}`);
          }
          if (request.activityWindowEnd) {
            parts.push(
              `(ClosedOnUtc le ${request.activityWindowEnd} or ClosedOnUtc eq null)`,
            );
          }

          conditions.push(`(${parts.join(' and ')})`);
        }

        if (request.financialAccountName) {
          const searchTerm = request.financialAccountName.toString().toLowerCase().replace(/'/g, "''");
          conditions.push(`contains(tolower(FinancialAccountName), '${searchTerm}')`);
        }
          
        const filterClause = conditions.length ? `filter(${conditions.join(' and ')})/` : '';
        const apply =
          `${filterClause}groupby((Direction,EntryType),aggregate(` +
          `Amount with sum as Total,` +
          `CashAmount with sum as TotalCash,` +
          `CardAmount with sum as TotalCard,` +
          `PixAmount with sum as TotalPix,` +
          `BankSlipAmount with sum as TotalBankSlip,` +
          `CheckAmount with sum as TotalCheck,` +
          `$count as Count))`;

        const ds = dxOdataService.fromOdata({
          url: url.treasury_service,
          metadata: 'OData/CashFlowEntries',
          params: {
            $apply: apply
          }
        });

        ds.load().then(rows => 
          patchState(store, { cashHomeChartsRows: rows }, setPropLoaded('loadCashHomeChartsData'))
        ).catch(error => patchState(store, { cashHomeChartsRows: [] },
          setPropError('loadCashHomeChartsData', error)
        ));
      };

      return {
        loadFinancialAccounts,
        loadOpenSessions,
        loadPendingSessions,
        loadMyRecentSessions,
        loadHomeCashSessions,
        accountCashSessionsDataSource,
        getCashSessionsDataSource,
        loadCashFlowReport,
        loadCashFlowAggregates,
        clearCashFlowAggregates,
        loadCashFlowEntriesByDay,
        clearCashFlowEntriesByDay,
        loadCashHomeChartsData,
        loadTenantDailyReport,
        loadTenantDailySeries,
        loadReconciliationDashboard,
        loadMyOpenSession,
        refreshCashSession,
        mergePersonNames,
        resetHome,
        loadSessionCashFlowEntries,
        cashFlowEntriesDataSource,
        invalidateSessionCashFlowEntries,
        openCashSession,
        closeCashSession,
        finalizeCashSession,
        reopenCashSession,
        createCashOperation,
        createCashTransfer,
        getCashSessionById,
        loadCashTurnContext,
        clearTurnContext,
        resetCashSessionReqState,
        resetCashOperationReqState,
        resetCashTurnContextReqState,
      };
    },
  ),
);
